import {
  shouldRunSyncHealthTick,
  shouldReprobeConnectivityOnTick,
  PullCatchupStats,
} from './sync-health-decisions';
import { shouldAutoResolveConflicts, shouldRestoreFromConflictArchive } from './sync-conflict-resolve-decisions';
import { shouldSkipResumeProbe } from './resume-probe-gate';
import { ConfigKey } from './config-keys';
import { bucketForIdentifier } from '@cf-shared/payment-provider-bucketing';
import { shouldForceDatabasesOfflineAtBoot } from '../app/boot-offline-seed';
import { decideCloudStateToast, CloudStateToast } from '../app/cloud-state-toast-policy';

/**
 * Connectivity, connection toasts and app resume — the plain half.
 *
 * The concerns below are grouped together because they are all driven by the
 * same pair of outside signals: whether the device can reach our server, and
 * whether the app is in front of the user.
 *
 *  - **The offline / back-online toasts.** The short message that appears when
 *    the connection is lost and again when it comes back. It is announced once,
 *    from app level, because a component that comes and goes cannot be trusted
 *    to see a transition.
 *  - **The connection and focus subscriptions**, plus the seed for a boot that
 *    STARTED offline and therefore never publishes a connection *change*.
 *  - **Soft resume and the foreground sync-health tick.** What runs when the
 *    user brings the app back to the front, and the same set of checks run
 *    every six minutes for a session that never leaves the front at all.
 *
 * There is no Angular in this file. Everything outside it arrives through
 * `ConnectivityResumePorts` below. That is what lets the node unit-test runner
 * load it — the connection services, the database layer, the toast host and the
 * telemetry client all pull in Angular or PouchDB and do not exist under node.
 *
 * Lifted out of `AppComponent` with no behavior change (first slice of deferred
 * finding df-7b66acbeb341). That component still owns WHEN each of these runs
 * during boot; see design-docs/boot-sequence.md.
 *
 * **Why the ports speak in booleans and not in the app's enums.** The states
 * involved — the cloud state, the connection state and the focus state — are
 * TypeScript enums declared in files that cannot be loaded under node. So
 * every `=== SomeEnum.Value` comparison is done by the adapter and this half
 * works in plain true/false. That is not a new convention: the pure helpers this
 * file calls (`decideCloudStateToast`, `shouldForceDatabasesOfflineAtBoot`)
 * already take booleans for exactly the same reason.
 */

/** The one thing this file needs from a subscription: a way to end it. */
export interface Unsubscribable {
  unsubscribe(): void;
}

/**
 * Everything outside this file that it needs. A unit test hands in a plain fake
 * object; `ConnectivityResumeService` hands in the real services.
 *
 * Several names here are deliberately identical to the methods they end up
 * calling (`ensureSyncsActive`, `ensurePullCatchupAsync` and so on). That is so
 * the source-text tripwires that pin the ORDER of those calls keep matching
 * real code after the move.
 */
export interface ConnectivityResumePorts {
  // ---- subscriptions ------------------------------------------------------

  /** Called whenever the device's reachability changes. */
  subscribeToConnectionState(onChanged: (isOnline: boolean) => void): Unsubscribable;
  /** Called whenever the app comes back to the front. Focus LOSS is not reported. */
  subscribeToFocusGained(onFocusGained: () => void): Unsubscribable;
  /** Called whenever the debounced cloud state changes. */
  subscribeToDebouncedCloudState(onChanged: (isOffline: boolean) => void): Unsubscribable;

  // ---- what the app currently believes -------------------------------------

  /** The debounced cloud state right now: is it Offline? */
  isCloudStateOffline(): boolean;
  /** The connection state right now: does the app believe it can reach us? */
  isConnectionOnline(): boolean;
  /** How long ago the last reachability probe settled, in milliseconds. */
  msSinceLastProbe(): number;
  /** Is the page in a background tab? A backgrounded tab must not run the tick. */
  isDocumentHidden(): boolean;
  /** The browser's own opinion (`navigator.onLine`), which is only a hint. */
  isBrowserOnline(): boolean;
  /** Is anybody signed in? */
  hasSignedInUser(): boolean;
  /** The signed-in user's id, used to decide which share of users a rollout has reached. */
  currentUserUid(): string | undefined;
  /** A monotonic millisecond clock, used only to measure how long things took. */
  nowMs(): number;

  // ---- probing --------------------------------------------------------------

  /** Wait for the first reachability probe of this boot to settle. Bounded by the adapter. */
  awaitFirstProbeAsync(): Promise<void>;
  /** Probe now, and say whether we are online. */
  verifyOnlineStatusAsync(expectOnline: boolean, confidence: 'expected' | 'suspected'): Promise<boolean>;

  // ---- the database layer ---------------------------------------------------

  /** Are the user's databases already marked Offline? */
  areUserDatabasesOffline(): boolean;
  /** Check the push checkpoint for drift. Fire-and-forget. */
  verifyPushCheckpointIntegrityAsync(): void;
  /** Restart any sync connection that died. Cheap no-op when they are healthy. */
  ensureSyncsActive(): void;
  /** Fetch anything a silently-dead pull longpoll missed. Undefined when there is no database yet. */
  ensurePullCatchupAsync(): Promise<PullCatchupStats> | undefined;
  /** Tell the database layer we are offline. */
  setAllDatabasesStatusOffline(): void;
  /** Flush calendar day-status updates a suspend left pending. */
  flushPendingReactiveStatusUpdates(): void;
  /** Run the conflict canary, restore from the conflict archive and drain the backlog when
   *  their rollouts allow. Fire-and-forget. */
  scanForConflictsAsync(autoResolveEnabled: boolean, archiveRestoreEnabled: boolean): void;
  /** Read a remote-config value out of the in-memory cache, synchronously. */
  getConfigValue(key: ConfigKey): unknown;

  // ---- toasts ---------------------------------------------------------------

  showWentOfflineToast(): void;
  showBackOnlineToast(): void;
  /** Has the user switched the offline toast off with "Never show again"? */
  isOfflineToastDismissed(): boolean;

  // ---- which browser tab this is --------------------------------------------

  readLastFocusedTabId(): string | null;
  writeLastFocusedTabId(tabId: string): void;
  currentTabId(): string;

  // ---- timers ---------------------------------------------------------------

  /**
   * Run `callback` once after `delayMs`. The returned value is only ever
   * compared against null, so a test may return anything non-null.
   */
  setTimer(callback: () => void, delayMs: number): unknown;

  // ---- side doors -----------------------------------------------------------

  /**
   * Reload the app, keeping the current page. **This never returns and never
   * rejects** — the page is replaced. Callers must treat it as the end of their
   * work, not as something to wait for and then carry on after.
   */
  reloadAppInPlace(): void;
  reportProblem(name: string, context: Record<string, unknown>): void;
  /**
   * Record that an automatic repair has ALREADY succeeded. Nothing is wrong and nobody has to
   * look — that is what separates it from `reportProblem`. See CLAUDE.md's three telemetry
   * severity tiers, and `design-docs/telemetry.md`.
   */
  reportInfo(name: string, context: Record<string, unknown>): void;
  trackEvent(name: string, properties: Record<string, unknown>): void;
}

/**
 * How often the foreground sync-health tick runs, in milliseconds.
 *
 * #3987 A5 (defect D5): a session that stays continuously in front of the user
 * gets no resume and no reconnect event, so the health checks those paths run
 * would never run at all, and a silently-dead pull connection would never be
 * caught. Six minutes.
 */
export const SYNC_HEALTH_TICK_MS = 6 * 60_000;

/**
 * What one run of the sync-recovery triad hands back to its caller. Both values are things the
 * caller could not get any other way once the triad has swallowed the calls: the pull catch-up's
 * promise, and how long `ensureSyncsActive` took. Callers that want neither ignore both.
 */
interface SyncRecoveryTriadResult {
  /** How long `ensureSyncsActive` took, in whole milliseconds. */
  ensureSyncsMs: number;
  /** The pull catch-up's promise. Undefined when there is no database to catch up. */
  catchupPromise: Promise<PullCatchupStats> | undefined;
}

export class ConnectivityResumeFlow {
  /**
   * Was the last announced cloud state Offline? Held as a plain boolean because
   * the only question ever asked of the state is whether it is Offline.
   * Undefined until the first announcement is seeded.
   */
  private lastAnnouncedOffline: boolean | undefined = undefined;

  /**
   * Was the offline toast ACTUALLY shown? It is false when the user permanently
   * dismissed it, so that we never announce a recovery from a loss we never
   * announced.
   */
  private offlineToastShown = false;

  /** Held so the boot paths can re-subscribe idempotently — see subscribeToConnectionAndFocusEvents. */
  private connectionStateSub: Unsubscribable | null = null;
  private focusStateSub: Unsubscribable | null = null;
  private cloudStateToastSub: Unsubscribable | null = null;

  /** Non-null once the sync-health tick chain has been started, so it is started only once. */
  private syncHealthTickHandle: unknown = null;

  constructor(private ports: ConnectivityResumePorts) {}

  // ===========================================================================
  // Offline / back-online toasts
  // ===========================================================================

  /**
   * F8 (#3991): the offline / back-online toasts are announced ONCE, from here.
   *
   * They used to live in cs-header-progress — a PER-PAGE component. That made the announcement
   * unreliable in a way that is easy to miss: if the state changed while no page header happened to
   * be mounted (mid-navigation, or on a header-less page such as login), the toast was simply never
   * raised — and it never recovered, because the next header to mount seeded its state as ALREADY
   * Offline and therefore saw no transition to announce. The "was an offline toast shown?" flag was
   * per-component too, so it reset on every page change and the back-online toast could go missing
   * on its own. A global announcement cannot be owned by a component that comes and goes.
   *
   * The header components are now purely visual. The pill and the 2px line render the STANDING
   * state (they are correct whenever they are mounted); this announces the TRANSITION.
   */
  subscribeToCloudStateToasts(): void {
    // unsubscribe first, so calling this twice cannot double-subscribe and announce every
    // transition twice. Its sibling subscribeToConnectionAndFocusEvents() already had to learn
    // this the hard way (a warm resume followed by a cold start called it again).
    this.cloudStateToastSub?.unsubscribe();
    this.lastAnnouncedOffline = this.ports.isCloudStateOffline();
    this.cloudStateToastSub = this.ports.subscribeToDebouncedCloudState((isOffline) => {
      const previousOffline = this.lastAnnouncedOffline;
      this.lastAnnouncedOffline = isOffline;
      this.announceCloudStateTransition(previousOffline, isOffline);
    });
  }

  announceCloudStateTransition(previousOffline: boolean | undefined, isOffline: boolean): void {
    // the decision is the pure `decideCloudStateToast` (table-tested) — the latch it reads is the
    // exact thing that has misbehaved before, so it does not live in an if-chain here
    const toastToShow = decideCloudStateToast({
      wasOffline: previousOffline === true,
      isOffline,
      alreadyAnnouncedOffline: this.offlineToastShown,
    });

    if (toastToShow === CloudStateToast.WentOffline) {
      // Ask whether the user switched this toast off, rather than inferring it from showToast's
      // return value. showToast returns undefined for TWO different reasons — "permanently
      // dismissed" and "the toast host is not mounted yet, so I queued it and will show it later"
      // — and only the first should suppress the matching back-online toast. Reading the return
      // value conflated them: on an offline cold boot the toast is queued while the splash is up,
      // so the user DOES see "You're offline now", but we would have recorded that they did not
      // and then silently withheld "Back online" — the exact asymmetry this latch exists to
      // prevent. Ask the setting, which is the thing that actually means "they switched it off".
      this.ports.showWentOfflineToast();
      // we announced the loss (or queued the announcement — same thing from the user's side, the
      // toast host shows it as soon as it mounts). Whether they are ALLOWED the matching recovery
      // toast is a separate question, and it is deliberately NOT answered here: the user can still
      // tap "Never show again" on the toast we just raised, and reading the setting now would miss
      // that. It is re-read at announce time below.
      this.offlineToastShown = true;
      return;
    }

    if (toastToShow === CloudStateToast.BackOnline) {
      // Suppress the recovery toast if the user has switched the offline toast off — that is the
      // very noise they turned off, and the standing pill keeps them informed instead. Read the
      // setting HERE, not when the offline toast was raised: they may have tapped "Never show
      // again" on that toast itself, which happens after we raised it.
      if (this.ports.isOfflineToastDismissed()) return;

      this.ports.showBackOnlineToast();
      this.offlineToastShown = false;
    }
  }

  // ===========================================================================
  // Connection and focus subscriptions
  // ===========================================================================

  /** Subscribe to the connection + focus events, and seed the offline state (F8b).
   *
   * Called from BOTH boot paths — cold start and warm resume. It must be idempotent, because warm
   * resume falls back to the cold-start path in its own catch: without the unsubscribe-first, that
   * fallback would leave TWO live subscriptions, and every connectivity flip would then run
   * `onOnlineStatusChangedAsync` twice — double ensureSyncsActive, double push-checkpoint check,
   * double pull-catchup. Same pattern as coach-settings' re-entrant ionViewWillEnter. */
  subscribeToConnectionAndFocusEvents(): void {
    this.connectionStateSub?.unsubscribe();
    this.focusStateSub?.unsubscribe();

    this.connectionStateSub = this.ports.subscribeToConnectionState((isOnline) => void this.onOnlineStatusChangedAsync(isOnline));
    this.focusStateSub = this.ports.subscribeToFocusGained(() => void this.onFocusGainedAsync());

    // F8b: a boot that STARTED offline publishes no state CHANGE, so the subscription above would
    // never fire. Seed it once the first probe settles. Fire-and-forget: it awaits the probe, which
    // must not block the rest of the boot.
    void this.seedOfflineCloudStateAfterFirstProbeAsync();
  }

  /**
   * F8b (#3991): tell the database layer we are offline on a boot that STARTED offline.
   *
   * The connection state machine only publishes a CHANGE, and OnlineStateService's initial
   * value is an Offline *placeholder*. So on a device booting in airplane mode the first
   * probe also settles on Offline, the state goes Offline -> Offline, and no
   * connectionStateChangedEvent is ever published. onOnlineStatusChangedAsync below is the
   * only thing that marks the databases offline, and it is purely event-driven — so it never
   * runs, the database layer latches on Syncing (commitQueriesAsync sets it, and the initial
   * sync's error handlers deliberately leave the state alone, deferring to
   * assureInitialSyncAsync's retry), and the header animates "Syncing" forever.
   *
   * We must AWAIT the first probe before reading the state: at the moment we subscribe, the
   * placeholder still says Offline even on a perfectly healthy online boot, so an immediate
   * read would mark every good boot offline. awaitFirstProbeAsync is bounded, so a hung probe
   * cannot stall this.
   */
  async seedOfflineCloudStateAfterFirstProbeAsync(): Promise<void> {
    await this.ports.awaitFirstProbeAsync();

    const mustSeed = shouldForceDatabasesOfflineAtBoot({
      connectionOnline: this.ports.isConnectionOnline(),
      cloudStateAlreadyOffline: this.ports.areUserDatabasesOffline(),
    });
    if (!mustSeed) return;

    console.log('App: boot settled offline and the databases had not been told — marking them offline');
    await this.onOnlineStatusChangedAsync(false);
  }

  // ===========================================================================
  // The sync-recovery triad
  // ===========================================================================

  /**
   * The three sync-recovery calls every reconnect-ish path runs, in the one order they are
   * allowed to run in. Call this instead of writing the three calls out again: their order is a
   * correctness rule — the push check before `ensureSyncsActive`, or it races the push that
   * `ensureSyncsActive` kicks into the Sentry 3ZD 409s. design-docs/sync-integrity.md,
   * "Recovery-triad invariant tripwire (#3988 F17)", is where that rule is written down; this
   * method is where it is now enforced (deferred finding df-1a1002a23c67).
   *
   * All three are fire-and-forget, so the order above is source order and the calls overlap.
   * The caller gets back the two things it cannot recover afterwards — the catch-up promise, and
   * how long `ensureSyncsActive` took. The duration is always measured, even for the callers
   * that ignore it: a clock read is free, and a flag to turn it off would put a branch inside
   * the one block whose whole purpose is to have none.
   */
  private runSyncRecoveryTriad(): SyncRecoveryTriadResult {
    this.ports.verifyPushCheckpointIntegrityAsync();

    const ensureSyncsStartMs = this.ports.nowMs();
    this.ports.ensureSyncsActive();
    const ensureSyncsMs = Math.round(this.ports.nowMs() - ensureSyncsStartMs);

    const catchupPromise = this.ports.ensurePullCatchupAsync();

    return { ensureSyncsMs, catchupPromise };
  }

  /** Event handler for online/offline events — registered towards OnlineStateService. */
  async onOnlineStatusChangedAsync(isOnline: boolean): Promise<void> {
    if (isOnline) {
      console.log('App: Online');
      // ensure database syncs are active (may have died while offline)
      if (this.ports.hasSignedInUser()) {
        // this path wants none of the triad's measurements back, so the catch-up promise is
        // discarded — the same fire-and-forget it always was
        void this.runSyncRecoveryTriad().catchupPromise;
      }
    } else {
      console.log('App: Offline');
      this.ports.setAllDatabasesStatusOffline();
    }
  }

  // ===========================================================================
  // Soft resume
  // ===========================================================================

  /** Called when the app comes back to the front. */
  async onFocusGainedAsync(): Promise<void> {
    // cross-tab detection: if another tab was focused since we last were, silently reload
    // to pick up fresh data from the shared IndexedDB
    const lastFocusedTab = this.ports.readLastFocusedTabId();
    const myTabId = this.ports.currentTabId();
    this.ports.writeLastFocusedTabId(myTabId);
    if (lastFocusedTab && lastFocusedTab !== myTabId) {
      // the page is replaced from here — this call never comes back, so there is deliberately
      // nothing after it. It is NOT awaited, because awaiting something that never settles reads
      // as if the rest of this method still runs afterwards.
      this.ports.reloadAppInPlace();
      return;
    }

    // recheck the online status.
    // S7 (#4027 Step 25): DEBOUNCED — a verify that completed less than
    // RECENT_PROBE_TRUST_MS (30s) ago is fresh enough for this AUTOMATIC resume
    // check, so skip the HEAD probe and reuse the published state. Rapid
    // background/foreground flips used to fire one un-debounced probe each.
    // User-initiated checks (the splash Retry buttons) still probe
    // unconditionally — a button whose meaning is "check again NOW" must check.
    //
    // #4318: the age of the last verify is not enough on its own — an OFFLINE
    // state is never reused, however fresh it is. On Android a backgrounded
    // WebView cannot use the network, so a verify that ran while the app was
    // asleep always fails and always says offline; reusing that on resume left
    // the app showing "Offline" for the rest of the session on a working phone.
    // shouldSkipResumeProbe carries the rule and the full explanation.
    const probeStartMs = this.ports.nowMs();
    const probeSkipped = shouldSkipResumeProbe({
      believedOnline: this.ports.isConnectionOnline(),
      msSinceLastProbe: this.ports.msSinceLastProbe(),
    });
    let isOnline: boolean;
    if (probeSkipped) {
      isOnline = this.ports.isConnectionOnline();
    } else {
      // probe expectation, not a feature gate (F9): this picks the expectedState + confidence that
      // verifyOnlineStatus should probe AGAINST, so the raw browser flag is exactly the right input.
      // A state that folded in the last probe result would make a stale Offline probe with
      // ('offline', 'expected') — logging a spurious "Expected offline, but got online" error on
      // every successful reconnect.
      isOnline = this.ports.isBrowserOnline()
        ? await this.ports.verifyOnlineStatusAsync(true, 'suspected')
        : await this.ports.verifyOnlineStatusAsync(false, 'expected');
    }
    const probeMs = Math.round(this.ports.nowMs() - probeStartMs);

    // if online and the user is logged in
    if (isOnline && this.ports.hasSignedInUser()) {
      // the sync connections may have died while backgrounded — iOS Safari kills them routinely.
      // this path keeps BOTH of the triad's return values: the catch-up promise, so the
      // resume_recovery event below can ride its stats without blocking the resume, and the
      // ensureSyncsActive duration, which is that event's ensure_syncs_ms field
      const { ensureSyncsMs, catchupPromise } = this.runSyncRecoveryTriad();

      // #4249: a suspend cancels the reactive day-status timers, so calendar
      // days whose data arrived just before the app went to sleep could sit
      // unflushed until the next pulled doc happened to come in. Flush them
      // now instead. Cheap no-op when nothing is pending.
      this.ports.flushPendingReactiveStatusUpdates();

      // O5 (#4027 Step 25): assemble the soft-resume timing event in the background —
      // the resume itself never waits on it
      void this.emitResumeRecoveryAsync(probeMs, probeSkipped, ensureSyncsMs, catchupPromise);
    }
  }

  /**
   * O5 (#4027 Step 25): the soft-resume path was completely untimed — probe latency,
   * ensureSyncs cost, and pull-catch-up cost/payload were invisible (observability gap O5).
   * Emits ONE resume_recovery Mixpanel event per focus-resume that reached the online +
   * logged-in branch. Awaits only the catch-up promise (usually a cooldown skip resolving
   * instantly); never throws into the resume path.
   */
  private async emitResumeRecoveryAsync(
    probeMs: number,
    probeSkipped: boolean,
    ensureSyncsMs: number,
    catchupPromise: Promise<PullCatchupStats> | undefined,
  ): Promise<void> {
    let catchup: PullCatchupStats | undefined;
    try {
      catchup = await catchupPromise;
    } catch (catchupErr: any) {
      // ensurePullCatchupAsync's internal try/catch starts AFTER its decision code, so a
      // rejection here can be a genuine bug in that pre-try slice (decidePullCatchup /
      // waitForRecreationGate) — before Step 25 it surfaced as an unhandled rejection, so
      // it must keep surfacing: report it instead of silently absorbing (the event below
      // still fires with catchup_ran false)
      this.ports.reportProblem('ResumeRecoveryCatchupRejected', {
        name: catchupErr?.name,
        message: catchupErr?.message,
      });
    }
    this.ports.trackEvent('resume_recovery', {
      probe_ms: probeMs,
      probe_skipped: probeSkipped,
      ensure_syncs_ms: ensureSyncsMs,
      catchup_ran: catchup?.ran ?? false,
      catchup_skip_reason: catchup?.skipReason,
      catchup_duration_ms: catchup?.durationMs,
      catchup_checked_count: catchup?.checkedCount,
      catchup_missed_count: catchup?.missedCount,
      catchup_body_bytes: catchup?.bodyBytes,
    });
  }

  // ===========================================================================
  // Foreground sync-health tick
  // ===========================================================================

  /**
   * Start the foreground sync-health tick chain. Safe to call more than once —
   * the second call does nothing, so a re-entrant boot cannot spawn parallel
   * timer chains. (Before this moved out of AppComponent, that guard sat at the
   * boot call site instead; it lives here now, the same way
   * subscribeToConnectionAndFocusEvents owns its own idempotency.)
   */
  startSyncHealthTick(): void {
    if (this.syncHealthTickHandle !== null) return;
    this.scheduleSyncHealthTick();
  }

  /**
   * #3987 A5 (defect D5): a foreground session gets no resume/online event, so it
   * would never run the resume path's health checks — the sync-recovery triad,
   * whose order rule lives with `runSyncRecoveryTriad` above — on a raw recursive
   * setTimeout chain. Both callees are internally cooldown-limited
   * (5-min push cooldown; 45s pull cooldown) and
   * settle-guarded, so a healthy idle tick costs at most a few tiny reads and
   * no extra network; the catch-up restarts live sync only when it actually
   * finds missed docs. Only the USER db wrapper is ticked (same wrapper
   * selection as the resume path); ensureSyncsActive is a cheap no-op for
   * healthy syncs. A hidden/offline/signed-out tick does no sync work but always
   * re-arms, so the chain survives for the whole session.
   *
   * df-eabc7656368c: a tick with no sync work to do is not always a tick with
   * nothing to do. When the app believes it is OFFLINE, this is the only thing
   * running that could find out it is wrong — so it checks the connection
   * instead. See shouldReprobeConnectivityOnTick for which cases that covers.
   */
  private scheduleSyncHealthTick(): void {
    this.syncHealthTickHandle = this.ports.setTimer(() => {
      const shouldRun = shouldRunSyncHealthTick(
        this.ports.isDocumentHidden(),
        this.ports.isConnectionOnline(),
        this.ports.hasSignedInUser(),
      );
      if (shouldRun) {
        // the tick wants none of the triad's measurements back
        void this.runSyncRecoveryTriad().catchupPromise;
        // #3987 B3: conflict canary — runs AFTER the push/pull checks; it is
        // internally gated to once per day per device, so this is a no-op on
        // almost every tick.
        //
        // #4314: the same call now also drains the conflict backlog, for the
        // share of users the rollout percent has reached. The percent is read
        // HERE, synchronously from the in-memory config cache, and passed in —
        // DatabaseWrapper must not read the config service itself, because that
        // service depends on CommonDbService and would close a dependency cycle.
        // Every unusable value reads as 0 — including a boolean, which the shape
        // guard refuses before the conversion — so the resolver stays off by default.
        const autoResolveEnabled = shouldAutoResolveConflicts(
          this.ports.getConfigValue(ConfigKey.ConflictAutoResolvePercent),
          this.ports.currentUserUid(),
          bucketForIdentifier,
        );
        // #6847: restoring closed log entries from the device's own archive has its own dial,
        // read the same way, so it can be widened before the resolver is switched back on
        const archiveRestoreEnabled = shouldRestoreFromConflictArchive(
          this.ports.getConfigValue(ConfigKey.ConflictArchiveRestorePercent),
          this.ports.currentUserUid(),
          bucketForIdentifier,
        );
        this.ports.scanForConflictsAsync(autoResolveEnabled, archiveRestoreEnabled);
      } else if (shouldReprobeConnectivityOnTick({
        documentHidden: this.ports.isDocumentHidden(),
        connectionStateIsOnline: this.ports.isConnectionOnline(),
        // the flag is only trusted in its "definitely no network" direction — the gate's own
        // comment says why, and this is a probe decision, not a feature gate (F9)
        browserSaysNoNetwork: !this.ports.isBrowserOnline(),
        hasUser: this.ports.hasSignedInUser(),
      })) {
        void this.reprobeConnectivityAsync();
      }
      this.scheduleSyncHealthTick();
    }, SYNC_HEALTH_TICK_MS);
  }

  /**
   * Ask the server whether it is reachable after all, on a tick where the app believes it is
   * offline (df-eabc7656368c). Fire-and-forget: nothing is waiting on the answer.
   *
   * Two details that look arbitrary and are not.
   *
   * It probes through verifyOnlineStatus rather than the cheaper bare reachability check. That is
   * the whole point: a successful check goes on to refresh the CouchDB session and publish the new
   * state, which is what makes sync start again. A bare reachability check would tell us the
   * network is fine and change nothing.
   *
   * It says it expects OFFLINE, with low confidence. We are asking a question here, not asserting
   * an answer, and the expectation only decides two side effects. Claiming to expect ONLINE would
   * log a spurious error line on the ordinary case where the phone really has no network, and
   * would schedule a second ping two seconds later — every six minutes, for the whole time
   * somebody is genuinely offline. Low confidence means a surprise (we are online after all) is
   * published without being complained about, which is exactly what we want.
   */
  private async reprobeConnectivityAsync(): Promise<void> {
    // read BEFORE the probe: afterwards this is reset to ~0 and says nothing. It is how long the
    // app had been sitting on the belief we are about to correct.
    const msSinceLastProbe = this.ports.msSinceLastProbe();

    const isOnline = await this.ports.verifyOnlineStatusAsync(false, 'suspected');
    if (!isOnline) return;

    // The check reached the server, but that is not the same as the app having ended up online, so
    // ask what actually got published before claiming a repair. verifyOnlineStatus reports what its
    // PING found; on an Offline-to-Online flip it then tries to refresh the CouchDB session, and if
    // that fails it deliberately writes the state back to Offline while still answering "online" to
    // its caller (see the auth-failure branch of setConnectionStateAsync). In that case nothing was
    // repaired: the app is still on the wrong belief and sync has not restarted. Reporting a
    // completed repair there would file an unresolved problem at the tier nobody looks at — and it
    // would spoil the one number this exists to produce, by over-counting exactly the case where the
    // app is still wrong. That failure is already reported by the auth branch itself, so there is
    // nothing to report here beyond staying quiet.
    if (!this.ports.isConnectionOnline()) return;

    // Info tier, not problem tier: by the time this line runs the repair has already happened —
    // verifyOnlineStatus published the corrected state and refreshed the session on its way here.
    // Worth counting, because it says how often the app is wrong about being offline, and nobody
    // has to act on any single one.
    this.ports.reportInfo('ConnectivityTickHealedOfflineBelief', {
      // Infinity before the first probe of the boot ever settled, which does not survive being
      // turned into telemetry — send nothing rather than a number that is not one.
      msSinceLastProbe: Number.isFinite(msSinceLastProbe) ? Math.round(msSinceLastProbe) : null,
    });
  }
}
